import { AuthCookieExpiryBufferMs, TOKEN } from 'features/auth/constants';
import {
  AUTH_BROADCAST_TYPE,
  subscribeAuthBroadcast,
} from 'features/auth/helpers/crossTab';
import {
  getAccessTokenExpiry,
  getAuthToken,
  handleAuthFailure,
  refreshAccessToken,
} from 'features/auth/helpers/helpers';
import { useEffect, useRef } from 'react';

const REFRESH_LEAD_BEFORE_COOKIE_EXPIRY_MS = 30_000;
// Refresh this long before expiry (ms): before the access token cookie drops (AuthCookieExpiryBufferMs ahead of `exp`),
// so requests never go out without a token
const REFRESH_BUFFER_MS =
  AuthCookieExpiryBufferMs + REFRESH_LEAD_BEFORE_COOKIE_EXPIRY_MS;
// Jitter: tab tới hạn sớm nhất gọi refresh trước, broadcast token mới, các tab còn lại bỏ gọi network.
const REFRESH_JITTER_MS = 5_000;
const MIN_DELAY_MS = 1_000; // sàn delay tránh lặp liên tục khi token sắp hết hạn

/**
 * Refresh access_token trước khi hết hạn, không chờ 401.
 * Web Locks + broadcast đảm bảo chỉ 1 tab gọi refresh; các tab khác nhận TOKEN_REFRESHED và reschedule.
 */
export const useProactiveTokenRefresh = () => {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let isActive = true;

    const clearTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = undefined;
    };

    const scheduleNext = () => {
      clearTimer();
      if (!isActive) return;

      const token = getAuthToken(TOKEN.ACCESS_TOKEN);
      if (!token) return; // chưa đăng nhập

      const expiresAt = getAccessTokenExpiry(token);
      if (expiresAt == null) return; // không decode được → dựa vào cơ chế 401

      const jitter = Math.random() * REFRESH_JITTER_MS;
      const delay =
        Math.max(expiresAt - Date.now() - REFRESH_BUFFER_MS, MIN_DELAY_MS) +
        jitter;

      timerRef.current = setTimeout(() => {
        void refreshAccessToken(token).then(newToken => {
          if (!isActive) return;
          if (newToken) {
            scheduleNext();
          } else {
            handleAuthFailure();
          }
        });
      }, delay);
    };

    // Tab khác vừa refresh xong → hẹn lại theo token mới, không tự gọi network
    const unsubscribe = subscribeAuthBroadcast(message => {
      if (message.type === AUTH_BROADCAST_TYPE.TOKEN_REFRESHED) {
        scheduleNext();
      }
    });

    scheduleNext();

    return () => {
      isActive = false;
      clearTimer();
      unsubscribe();
    };
  }, []);
};
